import { Component, effect, inject, signal } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { ECampaignStatus } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { CampaignsStore } from '@pages/campaigns/campaigns.store';
import { BannerCardComponent } from '@shared/components/banner-card/banner-card.component';
import { BaseComponent } from '@shared/components/base.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { endOfMonth, startOfMonth } from 'date-fns';
import { DashboardCalendarComponent } from './dashboard-calendar/dashboard-calendar.component';
import { DashboardCampaignsComponent } from './dashboard-campaigns/dashboard-campaigns.component';
import { DashboardStore } from './dashboard.store';

@Component({
  imports: [
    BannerCardComponent,
    TranslateModule,
    ContentContainerComponent,
    DashboardCalendarComponent,
    DashboardCampaignsComponent,
  ],
  providers: [DashboardStore, CampaignsStore],
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent extends BaseComponent {
  public readonly selectedDate = signal<Date>(new Date());
  public readonly selectedMonth = signal<Date>(new Date());

  public readonly currentDbUser$ = AuthService.currentDbUser;

  public readonly store = inject(DashboardStore);
  public readonly campaignsStore = inject(CampaignsStore);
  private readonly dateUtils = inject(DateUtilitiesService);

  constructor() {
    super();

    this.loadCampaignsForMonth();
    this.setupCampaignsEffect();
  }

  public onDateSelected(date: Date): void {
    this.selectedDate.set(date);
  }

  public onMonthChanged(month: Date): void {
    this.selectedMonth.set(month);
    this.loadCampaignsForMonth();
  }

  private loadCampaignsForMonth(): void {
    // campaignReadAll is a distinct, independently-grantable permission from campaignReadOwn (see
    // e.g. the sidebar's own separate gates for each) - a user with only campaignReadAll still
    // passes this /dashboard route's own guard (gated on unrelated news* permissions), so gating
    // this fetch on campaignReadOwn alone would silently leave their calendar empty.
    if (
      !this.permissionService.hasAnyPermission([this.EPermissions.campaignReadOwn, this.EPermissions.campaignReadAll])
    ) {
      return;
    }

    const monthStart = startOfMonth(this.selectedMonth());
    const monthEnd = endOfMonth(this.selectedMonth());

    this.campaignsStore.fetchCampaigns({
      params: {
        filters: {
          // eslint-disable-next-line camelcase
          end__gte: this.dateUtils.formatToApiDateTime(monthStart),
          // eslint-disable-next-line camelcase
          start__lte: this.dateUtils.formatToApiDateTime(monthEnd),
          // eslint-disable-next-line camelcase
          status__in: [ECampaignStatus.planned, ECampaignStatus.live, ECampaignStatus.ended],
        },
        joins: ['cta_templates', 'campaign_creators', 'campaign_creators.creator', 'customer'],
        limit: 1000,
        sort: [{ active: 'end', direction: 'asc' }],
      },
    });
  }

  private setupCampaignsEffect(): void {
    effect(() => {
      const campaigns = this.campaignsStore.campaigns$();

      if (campaigns.length > 0) {
        this.store.loadCampaignAnalytics({ campaigns });
      }
    });
  }
}
